import { UseQueryResult } from '@tanstack/react-query';
import { ApiResponse } from 'api/apiClient';
import { PaginationQueryParams } from 'types/queryTypes';

/**
 * Utility function to safely extract data from the query result.
 * It checks for undefined and returns the provided fallback if the result or data is not available.
 *
 * @template T - The type of data expected in the ApiResponse.
 * @param {UseQueryResult<ApiResponse<T>, unknown>} result - The result from the useQuery hook.
 * @param {T} fallback - The fallback value to return if data is not available.
 * @returns {T} - Returns the data from the response, or the fallback value if data is not available.
 */
export const extractQueryData = <T>(
  result: UseQueryResult<ApiResponse<T>, unknown>,
  fallback: T
): T => {
  return result?.data?.data ?? fallback;
};

/**
 * Fetches mocked data with pagination and optional query filtering.
 *
 * @template T - The type of the data items.
 * @param {T[]} data - The array of mocked data items.
 * @returns {Function} - An asynchronous function that accepts pagination parameters and returns a paginated response.
 */
export const fetchMockedData =
  <T extends Record<string, any>>(data: T[]) =>
  async ({
    page,
    limit,
    query = '',
  }: PaginationQueryParams): Promise<ApiResponse<T[]>> => {
    // Filter data based on the query
    let filteredData = data;

    if (query) {
      const trimmedQuery = query.trim().toLowerCase();
      filteredData = data.filter(item =>
        Object.values(item).some(value => {
          const itemType = typeof value;

          if (itemType === 'string')
            return value.toLowerCase().includes(trimmedQuery);
          if (itemType === 'number')
            return value.toString().includes(trimmedQuery);

          return false;
        })
      );
    }

    const startIndex = (page - 1) * limit;
    const paginatedData = filteredData.slice(startIndex, startIndex + limit);
    const totalPages = Math.ceil(filteredData.length / limit);

    // Simulate a delay of 1 second
    await new Promise(resolve => setTimeout(resolve, 1000));

    return {
      data: paginatedData,
      metadata: { limit, page, total: filteredData.length, totalPages },
      code: 200,
      message: 'OK',
    };
  };
